











































































































import { Component, Vue } from 'vue-property-decorator'
import CalendarAction from '@/views/Actions/CalendarAction.vue'
import { ActionAPI } from '@/api/backend-client'
import { Action } from '@/api/model/action'
import { ActionType } from '@/api/types'
import { User } from '@/api/model/user'
import { isAllowedUserForReferral } from '@/helpers/security'
import { apiDate, isDoneTask } from '@/helpers/overdue-actions'
import { fetchPagesInOrder, PagedResult } from '@/helpers/paging'
import moment from 'moment'

// Done tasks of one week, at most 3 × 100 (the API maximum per page); a cut-off is shown in the toolbar
const DONE_PAGE_SIZE = 100
const DONE_MAX_PAGES = 3

@Component({
    components: {
        CalendarAction,
    },
})

export default class Calendar extends Vue {
    actions: Action[] = []
    view: string = 'user'
    startDate: Date = null
    endDate: Date = null
    showTasks: boolean = true
    showMeetings: boolean = true
    showDone: boolean = true
    doneTasks: Action[] = []
    openLoading: boolean = false
    openFailed: boolean = false
    openRequest: number = 0
    doneLoading: boolean = false
    doneFailed: boolean = false
    doneIncomplete: boolean = false
    doneRequest: number = 0

    mounted() {
        if ('view' in this.$route.query) {
            this.changeView(this.$route.query.view as string)
        }

        if ('date' in this.$route.query) {
            this.setDate(new Date(this.$route.query.date as string))
        } else {
            this.setToday()
        }

        // Registered after the initial view is set, so opening the page loads once. The open actions are loaded for
        // all teams and filtered here, so only the done tasks (loaded per view, and only while shown) reload.
        // Hiding them goes through loadDoneTasks too: that discards a load that is still running.
        this.$watch('view', () => this.loadDoneTasks())
        this.$watch('showDone', () => this.loadDoneTasks())
        this.$watch('showTasks', () => this.loadDoneTasks())
    }

    get loading(): boolean {
        return this.openLoading || this.doneLoading
    }

    get availableViews(): string[] {
        return this.$currentUser.isTeamLead ? ['user', 'branches'] : ['user']
    }

    get availableViewsOptions() {
        return this.availableViews
            .map(availableView => ({
                label: this.$t(`actions-calendar.view.${availableView}`),
                value: availableView,
            }))
    }

    changeView(view: string): void {
        if (!this.availableViews.includes(view)) {
            return
        }

        this.view = view
    }

    async setToday(): Promise<void> {
        await this.setDate(new Date())
    }

    async setDate(date: Date): Promise<void> {
        this.startDate = moment(date).startOf('week').toDate()
        this.endDate = moment(this.startDate).add(6, 'days').endOf('day').toDate()
        await this.loadActions()
        this.scrollToOfficeHours()
    }

    async prevDate(): Promise<void> {
        this.startDate = moment(this.startDate).subtract(1, 'week').startOf('day').toDate()
        this.endDate = moment(this.startDate).add(6, 'days').endOf('day').toDate()
        await this.loadActions()
        this.scrollToOfficeHours()
    }

    async nextDate(): Promise<void> {
        this.startDate = moment(this.startDate).add(1, 'week').startOf('day').toDate()
        this.endDate = moment(this.startDate).add(6, 'days').endOf('day').toDate()
        await this.loadActions()
        this.scrollToOfficeHours()
    }

    /** The week's open and done items; each appears as soon as it arrives */
    async loadActions(): Promise<void> {
        await Promise.all([this.loadOpenActions(), this.loadDoneTasks()])
    }

    async loadOpenActions(): Promise<void> {
        const request = ++this.openRequest
        this.openLoading = true
        this.openFailed = false

        let open: Action[]|undefined
        try {
            // keyvue-api's `completed` filter is an exact SearchFilter: send 0/1 ("true" would match open actions)
            open = await ActionAPI.getAll({
                'completed': 0,
                'view': 'branches',
                'actionDate[after]': this.startDate,
                'actionDate[before]': this.endDate,
            })
        } catch (error) {
            // getAll returns undefined for most failures, but throws when the error body isn't JSON (an HTML 502)
            console.log(error)
        }

        // A newer load (another week) wins
        if (request !== this.openRequest) {
            return
        }

        this.actions = open || []
        this.openFailed = !open
        this.openLoading = false
    }

    /** Completed tasks of the week shown, per view; completed appointments are dropped (only tasks get a tick) */
    async loadDoneTasks(): Promise<void> {
        const request = ++this.doneRequest

        // Not shown, so not loaded; the counter also discards a load that is still running. The last list is kept,
        // not shown: a task completed between the two loads must not come back as open (see filteredActions).
        if (!this.showDone || !this.showTasks || !this.startDate) {
            this.doneFailed = false
            this.doneIncomplete = false
            this.doneLoading = false
            return
        }

        this.doneLoading = true

        const done: PagedResult<Action> = await fetchPagesInOrder(page => ActionAPI.getPaged({
            'completed': 1,
            'view': this.view,
            'actionDate[after]': apiDate(this.startDate),
            'actionDate[strictly_before]': apiDate(moment(this.startDate).add(7, 'days').toDate()),
            'order[actionDate]': 'asc',
            'pagination': true,
            'itemsPerPage': DONE_PAGE_SIZE,
            'page': page,
        }), DONE_MAX_PAGES)

        // A newer load (another week or view) wins
        if (request !== this.doneRequest) {
            return
        }

        this.doneTasks = done.items.filter(action => isDoneTask(action))
        this.doneFailed = done.failed && done.items.length === 0
        this.doneIncomplete = !this.doneFailed && (done.truncated || done.failed)
        this.doneLoading = false
    }

    get filteredActions(): Action[] {
        // Open and done items load at different moments, so a task completed in between can be in both lists:
        // the done copy wins
        const doneIds = new Set(this.doneTasks.map(action => action.id))
        const open = this.actions.filter(action => !doneIds.has(action.id))
        const done = this.showDone && this.showTasks ? this.doneTasks : []

        return [...open, ...done]
            .filter((action: Action) => {
                if (action.type !== ActionType.MEETING && !this.showTasks) {
                    return false
                } else if (action.type === ActionType.MEETING && !this.showMeetings) {
                    return false
                }

                if (!moment(action.actionDate).isSameOrAfter(this.startDate, 'day')) {
                    return false
                }

                if (!moment(action.actionDate).isSameOrBefore(this.endDate, 'day')) {
                    return false
                }

                if (this.view === 'user') {
                    return action.assignedTo.some((user: User) => {
                        return user.id === this.$currentUser.id
                    })
                }

                return true
            })
            .sort((a: Action, b: Action) => {
                return moment(a.actionDate).unix() - moment(b.actionDate).unix()
            })
    }

    get filteredActionsByDateAndHour() {
        const actionsByDate = {}

        this.filteredActions
            .forEach((action: Action) => {
                const actionDate = moment(action.actionDate).format('YYYY-MM-DD')
                if (!(actionDate in actionsByDate)) {
                    actionsByDate[actionDate] = {}
                }

                const actionHour = moment(action.actionDate).format('H')
                if (!(actionHour in actionsByDate[actionDate])) {
                    actionsByDate[actionDate][actionHour] = []
                }

                actionsByDate[actionDate][actionHour].push(action)
            })

        return actionsByDate
    }

    get week() {
        return moment(this.startDate).format('W')
    }

    get dates() {
        const dates = []

        let date = moment(this.startDate)
        while (date.isSameOrBefore(this.endDate, 'day')) {
            dates.push(date.format('YYYY-MM-DD'))
            date = moment(date).add('1', 'day')
        }

        return dates
    }

    get hours() {
        const hours = []

        for (let hour = 0; hour <= 23; hour++) {
            hours.push(hour)
        }

        return hours
    }

    actionsForDateAndHour(date: string, hour: string): Action[] {
        if (!(date in this.filteredActionsByDateAndHour)) {
            return []
        }

        if (!(hour in this.filteredActionsByDateAndHour[date])) {
            return []
        }

        return this.filteredActionsByDateAndHour[date][hour]
    }

    classesForDateAndHour(date: string, hour: string) {
        return {
            'bg-gray-100': !this.isOfficeHours(date, hour),
        }
    }

    isOfficeHours(date: string, hour: string) {
        const hourAsNumber = Number.parseInt(hour, 10)
        if (hourAsNumber < 9 || hourAsNumber >= 18) {
            return false
        }

        return true
    }

    async scrollToOfficeHours(): Promise<void> {
        await this.$nextTick(() => {
            const calendarEl = document.querySelector('.actions-calendar') as HTMLElement
            const calendarHeaderEl = calendarEl.querySelector(':first-child') as HTMLElement
            const firstOfficeHoursElement = calendarEl.querySelector('[data-is-office-hours]') as HTMLElement

            firstOfficeHoursElement.parentElement.scrollTop = firstOfficeHoursElement.offsetTop - 1.5 * calendarHeaderEl.clientHeight
        })
    }

    goToDetail(action: Action) {
        if (!isAllowedUserForReferral(this.$currentUser, action.referral) && this.$currentUser.betaOptInEnabled) {
            return this.$router.push({
                name: 'one_contact_detail',
                params: {
                    contactId: action.referral.contact.id,
                    toOne: this.$currentUser.betaOptInEnabled as unknown as string,
                },
            })
        }

        return this.$router.push({
            name: 'tasks_detail',
            params: {
                referralId: action.referral.id,
                contactId: action.referral.contact.id,
                toOne: this.$currentUser.betaOptInEnabled as unknown as string,
            },
        })
    }
}
